import React from "react";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  FileCheck2,
  KeyRound,
  LockKeyhole,
  Search,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { DEMO_ACTIONSTEP_MATTERS } from "../lib/outlookActionstepDemo";

const DEMO_URL = "/outlook-taskpane?mode=appointment";

const sections = [
  ["overview", "Overview"],
  ["workflow", "How it works"],
  ["test-data", "Test data"],
  ["actionstep-access", "Actionstep access"],
  ["security", "Security and privacy"],
  ["deployment", "Deployment and support"],
];

const accessRows = [
  {
    operation: "Matter search",
    access: "Read",
    purpose: "Find the correct Actionstep matter by name, reference or file number.",
    data: "Matter/action ID, name, description, reference and relevant dates.",
  },
  {
    operation: "Matter context",
    access: "Read",
    purpose: "Prefill the appearance and Aid Planner workflows for user review.",
    data: "Action type, assigned participant or owner, rate and next appearance information where available.",
  },
  {
    operation: "Appearance outcome",
    access: "Write",
    purpose: "Create a reviewed file note on the selected matter after explicit confirmation.",
    data: "Court, listing, funding, next date, outcome and notes entered by the user.",
  },
  {
    operation: "Follow-up task",
    access: "Write",
    purpose: "Create a user-confirmed legal-aid, extension or billing-review task.",
    data: "Task title, due date, priority, assignee and rate.",
  },
];

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-700">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">{title}</h2>
      {children && <p className="mt-4 text-base leading-7 text-slate-600">{children}</p>}
    </div>
  );
}

function DetailCard({ icon: Icon, title, children }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <h3 className="mt-5 text-base font-bold text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{children}</p>
    </article>
  );
}

export default function ClsConnectDocs() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <a href="#top" className="flex items-center gap-3" aria-label="CLS Connect documentation home">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-slate-950 text-xs font-black tracking-wider text-white">CLS</span>
            <span>
              <span className="block text-sm font-bold text-slate-950">CLS Connect</span>
              <span className="block text-xs text-slate-500">Integration documentation</span>
            </span>
          </a>
          <a
            href={DEMO_URL}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
          >
            Open demonstration
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </header>

      <main id="top">
        <section className="relative overflow-hidden border-b border-slate-200 bg-slate-950">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.38),transparent_38%),radial-gradient(circle_at_bottom_left,rgba(37,99,235,0.2),transparent_36%)]" />
          <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-300/30 bg-violet-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-violet-200">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                Actionstep integration review
              </div>
              <h1 className="mt-7 text-4xl font-black tracking-tight text-white sm:text-6xl">
                Court workflows connected to the correct matter.
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300">
                CLS Connect helps authorised legal and administrative users move reviewed court-appearance information between Actionstep, calendar appointments and Complete Law Support workflows without repeatedly retyping matter details.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a href={DEMO_URL} className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-slate-950 hover:bg-violet-50">
                  Try the safe demonstration
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <a href="#actionstep-access" className="inline-flex items-center rounded-xl border border-white/20 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                  Review requested access
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-[220px_minmax(0,1fr)] lg:py-20">
          <aside className="hidden lg:block">
            <nav className="sticky top-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Documentation sections">
              <p className="px-3 pb-3 text-xs font-bold uppercase tracking-wider text-slate-400">On this page</p>
              {sections.map(([id, label]) => (
                <a key={id} href={`#${id}`} className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-violet-50 hover:text-violet-800">
                  {label}
                </a>
              ))}
            </nav>
          </aside>

          <div className="min-w-0 space-y-20">
            <section id="overview" className="scroll-mt-8">
              <SectionHeading eyebrow="Purpose" title="An assisted workflow, not a replacement for Actionstep">
                Actionstep remains the matter-management system of record. CLS Connect supplies a focused interface for selecting a matter, preparing an appearance record and writing reviewed information back only when the user confirms it.
              </SectionHeading>
              <div className="mt-8 grid gap-5 md:grid-cols-3">
                <DetailCard icon={Search} title="Find the right matter">Search using a client name, matter name, file number or reference and review the result before continuing.</DetailCard>
                <DetailCard icon={CalendarDays} title="Capture the appearance">Use an Outlook or Google Calendar event to record the court, listing, funding, outcome and next court date.</DetailCard>
                <DetailCard icon={FileCheck2} title="Confirm the write-back">Create an Actionstep file note or follow-up task only after the authorised user reviews and confirms it.</DetailCard>
              </div>
            </section>

            <section id="workflow" className="scroll-mt-8">
              <SectionHeading eyebrow="Workflow" title="How the integration is intended to work">
                The integration reduces copying between systems while preserving user oversight at each decision point.
              </SectionHeading>
              <ol className="mt-8 grid gap-4">
                {[
                  ["1", "Authorise", "The user signs in through Actionstep OAuth and grants the requested read and write access under their existing Actionstep permissions."],
                  ["2", "Select", "The user searches for and selects the correct Actionstep matter. Restricted matters remain subject to Actionstep's access controls."],
                  ["3", "Review", "Matter basics prefill the CLS appearance or Aid Planner workflow. The user checks and completes the court, funding and follow-up information."],
                  ["4", "Confirm", "A separate confirmation creates the file note or task. Simply viewing or selecting a matter never writes to Actionstep."],
                  ["5", "Continue", "Calendar and internal billing-review actions remain separate from the Actionstep API and retain their own confirmation steps."],
                ].map(([number, title, body]) => (
                  <li key={number} className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-[48px_1fr] sm:items-start">
                    <span className="grid h-12 w-12 place-items-center rounded-xl bg-slate-950 text-sm font-black text-white">{number}</span>
                    <div>
                      <h3 className="font-bold text-slate-950">{title}</h3>
                      <p className="mt-1 text-sm leading-6 text-slate-600">{body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="test-data" className="scroll-mt-8">
              <SectionHeading eyebrow="Demonstration" title="Sample matters available for testing">
                The public demonstration contains the fictional matters below. Search by the sample client name or file number, then select the result to test the appearance and outcome workflow. No Actionstep account or live client data is used.
              </SectionHeading>
              <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] border-collapse text-left text-sm">
                    <thead className="bg-violet-700 text-white">
                      <tr>
                        <th className="px-5 py-4 font-bold">Sample client</th>
                        <th className="px-5 py-4 font-bold">File number</th>
                        <th className="px-5 py-4 font-bold">Matter and court</th>
                        <th className="px-5 py-4 font-bold">Appearance</th>
                        <th className="px-5 py-4 font-bold">Funding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {DEMO_ACTIONSTEP_MATTERS.map(({ externalId, client, matter }) => (
                        <tr key={externalId} className="align-top">
                          <td className="px-5 py-5 font-bold text-slate-950">{client.fullName}</td>
                          <td className="px-5 py-5"><code className="rounded bg-slate-100 px-2 py-1 font-bold text-violet-800">{client.fileNumber}</code></td>
                          <td className="px-5 py-5 leading-6 text-slate-600">
                            <span className="block font-semibold text-slate-800">{matter.summary}</span>
                            {matter.court}
                          </td>
                          <td className="px-5 py-5 leading-6 text-slate-600">
                            <span className="block font-semibold text-slate-800">{matter.appearanceType}</span>
                            {matter.nextAppearanceDate}
                          </td>
                          <td className="px-5 py-5 leading-6 text-slate-600">
                            {matter.fundingType}
                            {matter.grantOfAidNumber && <span className="block text-xs text-slate-500">{matter.grantOfAidNumber}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <a href={DEMO_URL} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-violet-700 hover:text-violet-900">
                Open the demonstration and search a sample matter
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </section>

            <section id="actionstep-access" className="scroll-mt-8">
              <SectionHeading eyebrow="Actionstep API" title="Requested access and its purpose">
                CLS Connect requests read and write scopes. Access is limited to the functions below and remains constrained by the authenticated user’s Actionstep permissions.
              </SectionHeading>
              <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                    <thead className="bg-slate-950 text-white">
                      <tr>
                        <th className="px-5 py-4 font-bold">Operation</th>
                        <th className="px-5 py-4 font-bold">Access</th>
                        <th className="px-5 py-4 font-bold">Purpose</th>
                        <th className="px-5 py-4 font-bold">Data involved</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {accessRows.map((row) => (
                        <tr key={row.operation} className="align-top">
                          <td className="px-5 py-5 font-bold text-slate-950">{row.operation}</td>
                          <td className="px-5 py-5"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${row.access === "Read" ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-700"}`}>{row.access}</span></td>
                          <td className="px-5 py-5 leading-6 text-slate-600">{row.purpose}</td>
                          <td className="px-5 py-5 leading-6 text-slate-600">{row.data}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                <div className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-700" aria-hidden="true" />
                  <div>
                    <h3 className="font-bold text-emerald-950">Operations deliberately excluded</h3>
                    <p className="mt-2 text-sm leading-6 text-emerald-900/80">CLS Connect does not delete or close matters, modify trust or accounting records, issue invoices, submit legal-aid claims, or make an Actionstep write without an explicit user action.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="security" className="scroll-mt-8">
              <SectionHeading eyebrow="Controls" title="Security, privacy and data handling">
                The production Actionstep connection will be enabled only after Actionstep approval and completion of the production OAuth security controls described here.
              </SectionHeading>
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                <DetailCard icon={KeyRound} title="OAuth authorisation">Users authenticate with Actionstep and grant access directly. CLS Connect never asks for or stores the user’s Actionstep password.</DetailCard>
                <DetailCard icon={LockKeyhole} title="Protected credentials">The production client secret and authorisation-code exchange will be handled by a protected server-side endpoint and will not be shipped in browser code.</DetailCard>
                <DetailCard icon={ShieldCheck} title="Least privilege">Only the matter information required for selection and prefilling is requested. Writes are limited to confirmed file notes and tasks.</DetailCard>
                <DetailCard icon={Workflow} title="Data separation">Microsoft and Google calendar actions use their separately authorised APIs. Internal billing intake creates a review draft and does not submit an Actionstep invoice or claim.</DetailCard>
              </div>
              <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm leading-6 text-amber-950">
                <strong>Review environment:</strong> the public demonstration uses sample data and does not access Actionstep. Live Actionstep data will not be enabled for general users until production API credentials are issued and the server-side OAuth exchange has been deployed and verified.
              </div>
            </section>

            <section id="deployment" className="scroll-mt-8">
              <SectionHeading eyebrow="Availability" title="Deployment, installation and support">
                CLS Connect is delivered as a secure web application and Outlook add-in. The planned market-ready date is 1 September 2026, subject to Actionstep approval and final production verification.
              </SectionHeading>
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="font-bold text-slate-950">Installation</h3>
                  <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                    <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-violet-700" />The Outlook add-in manifest points to the hosted CLS Connect task pane.</li>
                    <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-violet-700" />Authorised users connect their own Actionstep account through OAuth.</li>
                    <li className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 flex-none text-violet-700" />Training and controlled pilot onboarding precede wider availability.</li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="font-bold text-slate-950">Support and ownership</h3>
                  <p className="mt-4 text-sm leading-6 text-slate-600">Developed and operated by Complete Law Support for authorised staff and participating legal practices.</p>
                  <a href="mailto:admin@completelawsupport.com" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-violet-700 hover:text-violet-900">
                    admin@completelawsupport.com
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                  <p className="mt-5 text-xs text-slate-400">Documentation version 1.0 · Updated 3 August 2026</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <p>© 2026 Complete Law Support. CLS Connect integration documentation.</p>
          <a href="https://completelawsupport.com" className="font-semibold text-slate-700 hover:text-violet-700">Complete Law Support</a>
        </div>
      </footer>
    </div>
  );
}
